// =============================================================================
// components/recurring/CommitmentDialog.jsx — Phase 82.12
//
// Shared create/edit dialog for a recurring commitment. Ports the CRUD
// dialog surface out of the legacy `pages/Recurring.js` (947 LOC) into a
// focused reusable component that the new Recurring hub (`RecurringPage.js`)
// can mount both from its header ("Add commitment") and from its detail
// drawer ("Edit").
//
// Parity notes with legacy Recurring.js
// -------------------------------------
//   • Kinds preserved: income | expense | investment | debt_payment | transfer.
//   • Category defaults follow the legacy KINDS map so pre-fills match.
//   • Frequency options match legacy FREQ (monthly / fortnightly / weekly /
//     quarterly / yearly), and switching frequency cascades a fresh
//     `next_due` proposal so the user isn't stuck editing dates twice.
//   • Mandatory links for `investment` (wealth) and `debt_payment` (liability
//     or credit-card account) mirror legacy validation.
//   • On insert we write the `kind` + `linked_*` columns; if the DB is
//     pre-migration and rejects those columns we fall back to a legacy row
//     shape so the dialog still works in older environments.
//   • Toast copy is kept short and non-preachy per product voice.
//
// This dialog does NOT own list state. It receives `initial` (a normalised
// commitment row or null) and calls `onSaved()` when the parent should
// re-fetch. This keeps ownership of the recurring list inside the parent
// hook (`useRecurringData` in RecurringPage.js).
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { invalidateBriefing } from '@/lib/briefingCache';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CategorySelect } from '@/components/CategorySelect';
import { Loader2, Briefcase, Wallet, TrendingUp, CreditCard, ArrowLeftRight } from 'lucide-react';

// ─── Constants (mirror legacy Recurring.js) ─────────────────────────────────
const FREQ = [
  { value: 'monthly',     label: 'Monthly',       days: 30  },
  { value: 'fortnightly', label: 'Fortnightly',   days: 14  },
  { value: 'weekly',      label: 'Weekly',        days: 7   },
  { value: 'quarterly',   label: 'Quarterly',     days: 92  },
  { value: 'half_yearly', label: 'Half-yearly',   days: 182 },
  { value: 'yearly',      label: 'Yearly',        days: 365 },
];

// Kind meta — same shape/values as legacy Recurring.js so downstream
// analytics and reflections don't need to change.
const KINDS = {
  income: {
    label: 'Income',
    icon: Briefcase,
    helperText: 'Salary, freelance retainer, rent received, dividends, pension.',
    namePlaceholder: 'Monthly salary, freelance retainer, rental income',
    defaultCategory: 'Salary',
    categoriesFor: 'income',
  },
  expense: {
    label: 'Expense',
    icon: Wallet,
    helperText: 'Rent, Netflix, school fees, insurance premium, mobile bill.',
    namePlaceholder: 'Netflix, Rent, Internet',
    defaultCategory: 'Subscriptions',
    categoriesFor: 'expense',
  },
  investment: {
    label: 'Investment',
    icon: TrendingUp,
    helperText: 'Monthly SIP, 401(k) / super contribution, index fund top-up.',
    namePlaceholder: 'Index fund SIP, 401(k), super contribution',
    defaultCategory: 'Mutual Fund',
    categoriesFor: 'investment',
  },
  debt_payment: {
    label: 'Debt payment',
    icon: CreditCard,
    helperText: 'Home loan / mortgage, car loan, credit card payment.',
    namePlaceholder: 'Home loan, car loan, credit card payment',
    defaultCategory: 'Loan Payment',
    categoriesFor: 'expense',
  },
  transfer: {
    label: 'Transfer',
    icon: ArrowLeftRight,
    helperText: 'Salary-day sweep to savings, monthly transfer to investment account, family remittance.',
    namePlaceholder: 'Salary-day sweep, monthly investment transfer',
    defaultCategory: 'Transfer',
    categoriesFor: 'expense',
  },
};

const KIND_ORDER = ['expense', 'income', 'investment', 'debt_payment', 'transfer'];

// Utility — add `days` days to the current date and return YYYY-MM-DD.
function addDaysISO(days, base = new Date()) {
  const x = new Date(base);
  x.setDate(x.getDate() + days);
  return x.toISOString().slice(0, 10);
}

// Convert an existing commitment row (from RecurringPage's normalised
// shape or a raw recurring_expenses row) into the form's shape.
function seedFromInitial(initial) {
  if (!initial) {
    return {
      kind: 'expense',
      name: '',
      amount: '',
      category: KINDS.expense.defaultCategory,
      frequency: 'monthly',
      next_due: addDaysISO(30),
      active: true,
      linked_wealth_id: '',
      linked_liability_id: '',
      linked_account_id: '',
    };
  }
  // Support both normalised RecurringPage rows and raw supabase rows.
  const k = initial.kind || 'expense';
  return {
    kind: KINDS[k] ? k : 'expense',
    name: initial.name || '',
    amount: initial.amount != null ? String(initial.amount) : '',
    category: initial.category || KINDS[k]?.defaultCategory || 'Subscriptions',
    frequency: initial.frequency || 'monthly',
    next_due: initial.next_due || initial.nextDate || addDaysISO(30),
    active: initial.active !== false,
    linked_wealth_id:    initial.linked_wealth_id    || '',
    linked_liability_id: initial.linked_liability_id || '',
    linked_account_id:   initial.linked_account_id   || '',
  };
}

// Load link targets (wealth / liability / credit-card accounts). Legacy
// dialog needs these to enforce mandatory linking for `investment` and
// `debt_payment` kinds. All calls are best-effort — a missing table just
// yields an empty list.
function useLinkTargets(userId) {
  const [wealthChoices,     setWealthChoices]     = useState([]);
  const [liabilityChoices,  setLiabilityChoices]  = useState([]);
  const [creditCardChoices, setCreditCardChoices] = useState([]);
  // Phase 84.23.8 — bank/cash/wallet accounts used as the "From account"
  // in the debt-payment picker (beta users asked "from which bank to
  // which loan?"). Fetched here so all link targets live in one hook.
  const [payerChoices,      setPayerChoices]      = useState([]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [w, l, c, p] = await Promise.all([
          supabase.from('wealth_holdings').select('id, name, kind').order('name'),
          supabase.from('liabilities').select('id, name, kind').order('name'),
          supabase.from('accounts').select('id, name, last4').eq('type', 'credit_card').order('name'),
          supabase.from('accounts').select('id, name, type, last4').in('type', ['bank', 'cash', 'wallet']).order('name'),
        ]);
        if (cancelled) return;
        setWealthChoices(w?.data || []);
        setLiabilityChoices(l?.data || []);
        setCreditCardChoices(c?.data || []);
        setPayerChoices(p?.data || []);
      } catch (_e) {
        // Tables not present — leave arrays empty; save() will still fail
        // the validation gate below with a clear message.
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return { wealthChoices, liabilityChoices, creditCardChoices, payerChoices };
}

// ─── Component ──────────────────────────────────────────────────────────────
export function CommitmentDialog({
  open,
  onOpenChange,
  initial = null,          // existing commitment for edit; null → create
  onSaved,                 // parent refresh hook
}) {
  const { user } = useAuth();
  const mode = initial ? 'edit' : 'create';
  const editingId = initial?.id || null;

  const [form, setForm] = useState(() => seedFromInitial(initial));
  const [saving, setSaving] = useState(false);

  // Reseed the form whenever `initial` changes (parent switches between
  // create and edit against the same mounted dialog).
  useEffect(() => {
    setForm(seedFromInitial(initial));
  }, [initial]);

  const { wealthChoices, liabilityChoices, creditCardChoices, payerChoices } = useLinkTargets(user?.id);
  const kindMeta = KINDS[form.kind] || KINDS.expense;

  // Cascade next_due when frequency changes so the field never drifts
  // silently. If the user has already set a specific date we still push
  // forward — this matches legacy behaviour.
  const setFrequency = (value) => {
    const freq = FREQ.find((f) => f.value === value) || FREQ[0];
    setForm((f) => ({ ...f, frequency: value, next_due: addDaysISO(freq.days) }));
  };

  const switchKind = (value) => {
    setForm((f) => ({
      ...f,
      kind: value,
      category: KINDS[value]?.defaultCategory || f.category,
      // Reset link fields when switching kinds — a link only makes sense
      // for the current kind.
      linked_wealth_id: '',
      linked_liability_id: '',
      linked_account_id: '',
    }));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim() || !form.amount || Number(form.amount) <= 0) {
      toast.error('Add a name and a positive amount.');
      return;
    }
    // Mandatory-link parity with legacy validation.
    if (form.kind === 'investment' && !form.linked_wealth_id) {
      toast.error('Pick the wealth holding this investment funds.');
      return;
    }
    // Phase 84.23.8 — debt_payment now requires the loan (TO). The
    // `linked_account_id` field is the FROM (bank/cash) — optional but
    // encouraged so 'from → to' is visible everywhere the commitment
    // renders.
    if (form.kind === 'debt_payment' && !form.linked_liability_id) {
      toast.error('Pick the loan this payment reduces. For credit-card bill payments use the Transfer kind instead.');
      return;
    }
    setSaving(true);
    try {
      const baseRow = {
        user_id: user.id,
        name: form.name.trim(),
        amount: Number(form.amount),
        category: form.category || kindMeta.defaultCategory,
        frequency: form.frequency,
        next_due: form.next_due,
        active: !!form.active,
      };
      const rowWithKind = {
        ...baseRow,
        kind: form.kind,
        linked_wealth_id:    form.kind === 'investment'   ? (form.linked_wealth_id    || null) : null,
        linked_liability_id: form.kind === 'debt_payment' ? (form.linked_liability_id || null) : null,
        linked_account_id:   form.kind === 'debt_payment' ? (form.linked_account_id   || null) : null,
      };
      let error = null;
      if (editingId) {
        ({ error } = await supabase
          .from('recurring_expenses')
          .update(rowWithKind)
          .eq('id', editingId));
      } else {
        ({ error } = await supabase
          .from('recurring_expenses')
          .insert(rowWithKind));
      }
      // Pre-Phase-60 environments: fall back to base row if the DB rejects
      // the extended shape.
      if (error && /column .*(kind|linked_).* does not exist/i.test(error.message || '')) {
        if (editingId) {
          ({ error } = await supabase.from('recurring_expenses').update(baseRow).eq('id', editingId));
        } else {
          ({ error } = await supabase.from('recurring_expenses').insert(baseRow));
        }
      }
      if (error) throw error;
      // Phase 84.5 — invalidate the Financial Briefing cache so the
      // Overview page recomputes NBAs from the new commitment set on
      // the next visit (fixes stale "Review flagged commitment" pointing
      // to a rule the user already deleted or edited).
      invalidateBriefing();
      toast.success(editingId ? `${kindMeta.label} updated.` : `${kindMeta.label} added.`);
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      const m = (err?.message || '').toLowerCase();
      if ((form.kind === 'income' || form.kind === 'transfer') && /recurring_expenses_kind_check|check constraint/.test(m)) {
        toast.error(`Recurring ${kindMeta.label.toLowerCase()} needs a database update. Ask an admin to apply the latest migration.`);
      } else {
        toast.error(err.message || 'Could not save.');
      }
    } finally {
      setSaving(false);
    }
  };

  const linkedTargetLabel = useMemo(() => {
    if (form.kind === 'investment')   return 'Funds which wealth holding?';
    if (form.kind === 'debt_payment') return 'Applies to which loan or credit card?';
    return '';
  }, [form.kind]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="recurring-commitment-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit commitment' : 'Add a new commitment'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4">
          {/* Kind selector — hidden on edit to keep type immutable (matches
              legacy dialog which only allowed field edits, not kind swaps). */}
          {mode === 'create' && (
            <div>
              <Label className="mb-1.5 block">Type</Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="recurring-commitment-kind-row">
                {KIND_ORDER.map((k) => {
                  const meta = KINDS[k];
                  const Icon = meta.icon;
                  const active = form.kind === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => switchKind(k)}
                      data-testid={`recurring-commitment-kind-${k}`}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-medium transition-colors ${
                        active
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">{kindMeta.helperText}</p>
            </div>
          )}

          <div>
            <Label htmlFor="commitment-name" className="mb-1.5 block">Name</Label>
            <Input
              id="commitment-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={kindMeta.namePlaceholder}
              data-testid="recurring-commitment-name"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="commitment-amount" className="mb-1.5 block">Amount</Label>
              <Input
                id="commitment-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                data-testid="recurring-commitment-amount"
              />
            </div>
            <div>
              <Label htmlFor="commitment-frequency" className="mb-1.5 block">Frequency</Label>
              <Select value={form.frequency} onValueChange={setFrequency}>
                <SelectTrigger id="commitment-frequency" data-testid="recurring-commitment-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQ.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="commitment-category" className="mb-1.5 block">Category</Label>
              <CategorySelect
                id="commitment-category"
                type={kindMeta.categoriesFor}
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                testId="recurring-commitment-category"
              />
            </div>
            <div>
              <Label htmlFor="commitment-next-due" className="mb-1.5 block">
                {form.kind === 'income' ? 'Next expected' : 'Next due'}
              </Label>
              <Input
                id="commitment-next-due"
                type="date"
                value={form.next_due}
                onChange={(e) => setForm((f) => ({ ...f, next_due: e.target.value }))}
                data-testid="recurring-commitment-next-due"
              />
            </div>
          </div>

          {/* Linked target — only relevant for investment / debt_payment. */}
          {(form.kind === 'investment' || form.kind === 'debt_payment') && (
            <div className="space-y-3">
              {form.kind === 'investment' && (
                <div>
                  <Label className="mb-1.5 block">{linkedTargetLabel}</Label>
                  <Select
                    value={form.linked_wealth_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, linked_wealth_id: v }))}
                  >
                    <SelectTrigger data-testid="recurring-commitment-linked-wealth">
                      <SelectValue placeholder="Pick a wealth holding" />
                    </SelectTrigger>
                    <SelectContent>
                      {wealthChoices.length === 0 ? (
                        <SelectItem value="__none" disabled>No wealth holdings yet — add one under Accounts.</SelectItem>
                      ) : (
                        wealthChoices.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.kind === 'debt_payment' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Phase 84.23.8 — beta users asked to see BOTH sides of a loan
                      EMI: which bank pays, which loan gets reduced. */}
                  <div>
                    <Label className="mb-1.5 block">From account</Label>
                    <Select
                      value={form.linked_account_id}
                      onValueChange={(v) => setForm((f) => ({ ...f, linked_account_id: v }))}
                    >
                      <SelectTrigger data-testid="recurring-commitment-pay-from">
                        <SelectValue placeholder="Bank / cash / wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {payerChoices.length === 0 ? (
                          <SelectItem value="__none" disabled>Add a bank / cash account first.</SelectItem>
                        ) : (
                          payerChoices.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name}{a.last4 ? ` ····${a.last4}` : ''}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">To loan</Label>
                    <Select
                      value={form.linked_liability_id}
                      onValueChange={(v) => setForm((f) => ({ ...f, linked_liability_id: v }))}
                    >
                      <SelectTrigger data-testid="recurring-commitment-linked-liability">
                        <SelectValue placeholder="Which loan?" />
                      </SelectTrigger>
                      <SelectContent>
                        {liabilityChoices.length === 0 ? (
                          <SelectItem value="__none" disabled>No loans yet — add one under Accounts → Loans.</SelectItem>
                        ) : (
                          liabilityChoices.map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-muted-foreground sm:col-span-2 leading-relaxed">
                    <span className="font-semibold text-foreground">Paying a credit-card bill?</span>{' '}
                    Use the <span className="font-semibold">Transfer</span> kind instead — from your bank account to your credit card. 2120 treats card-bill payments as transfers, not expenses (the swipes at merchants are the expenses).
                    {creditCardChoices.length > 0 && ' You have a credit card set up already.'}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
              data-testid="recurring-commitment-cancel"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="recurring-commitment-save">
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {mode === 'edit' ? 'Save changes' : 'Add commitment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CommitmentDialog;
